import React from 'react';
import { useLegalEditor } from './useLegalEditor';
import LegalSectionsEditor from './LegalSectionsEditor';
import LegalPublishModal from './LegalPublishModal';

export const LegalDocumentEditor: React.FC = () => {
  const model = useLegalEditor();
  const {
    activeDoc, setActiveDoc, loading, saving, notification, setNotification,
    editingContent, setEditingContent, consentStats, hasUnsavedChanges,
    handleSave, openPublishModal,
  } = model;

  if (loading) {
    return (
      <div className="rp-bento-card mb-6">
        <div className="text-center py-8" style={{ color: 'var(--rp-text-secondary)' }}>
          Loading legal documents...
        </div>
      </div>
    );
  }

  return (
    <div className="rp-bento-card mb-6">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-xl font-semibold" style={{ color: 'var(--rp-text)' }}>
            Legal Documents
          </h2>
          <p className="text-sm mt-1" style={{ color: 'var(--rp-text-secondary)' }}>
            Manage Terms of Service and Privacy Policy. Changes sync to both the website and iOS app.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasUnsavedChanges && (
            <span className="px-2 py-1 rounded text-xs font-semibold bg-yellow-500/20 text-yellow-400 border border-yellow-400/30">
              UNSAVED
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={saving || !hasUnsavedChanges}
            className="px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-50"
            style={{ backgroundColor: 'var(--rp-primary)', color: '#fff' }}
          >
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
          <button
            onClick={openPublishModal}
            className="px-4 py-2 rounded-lg text-sm font-semibold transition-all text-white"
            style={{ backgroundColor: 'var(--admin-primary)' }}
          >
            Publish New Version
          </button>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`admin-notice ${notification.type === 'error' ? 'admin-notice-error' : 'admin-notice-success'} mb-4`}>
          <span className="admin-notice-message flex-1">{notification.message}</span>
          <button onClick={() => setNotification(null)} aria-label="Dismiss notification" className="admin-notice-dismiss">×</button>
        </div>
      )}

      {/* Document Type Tabs */}
      <div className="flex gap-2 mb-6">
        {(['terms_of_service', 'privacy_policy'] as const).map(docType => (
          <button
            key={docType}
            onClick={() => setActiveDoc(docType)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              activeDoc === docType ? 'shadow-lg' : 'hover:bg-white/5'
            }`}
            style={
              activeDoc === docType
                ? { backgroundColor: 'var(--rp-primary)', color: '#fff' }
                : { color: 'var(--rp-text-secondary)', border: '1px solid var(--rp-border)' }
            }
          >
            {docType === 'terms_of_service' ? 'Terms of Service' : 'Privacy Policy'}
          </button>
        ))}
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="p-3 rounded-lg border" style={{ borderColor: 'var(--rp-border)', backgroundColor: 'rgba(255,255,255,0.03)' }}>
          <div className="text-xs font-semibold mb-1" style={{ color: 'var(--rp-text-secondary)' }}>Current Version</div>
          <div className="text-lg font-bold" style={{ color: 'var(--rp-text)' }}>
            v{editingContent?.version || '—'}
          </div>
        </div>
        <div className="p-3 rounded-lg border" style={{ borderColor: 'var(--rp-border)', backgroundColor: 'rgba(255,255,255,0.03)' }}>
          <div className="text-xs font-semibold mb-1" style={{ color: 'var(--rp-text-secondary)' }}>Last Updated</div>
          <div className="text-lg font-bold" style={{ color: 'var(--rp-text)' }}>
            {editingContent?.last_updated || '—'}
          </div>
        </div>
        <div className="p-3 rounded-lg border" style={{ borderColor: 'var(--rp-border)', backgroundColor: 'rgba(255,255,255,0.03)' }}>
          <div className="text-xs font-semibold mb-1" style={{ color: 'var(--rp-text-secondary)' }}>Users Consented</div>
          <div className="text-lg font-bold" style={{ color: 'var(--admin-success)' }}>
            {consentStats?.consented_current ?? '—'}
          </div>
        </div>
        <div className="p-3 rounded-lg border" style={{ borderColor: 'var(--rp-border)', backgroundColor: 'rgba(255,255,255,0.03)' }}>
          <div className="text-xs font-semibold mb-1" style={{ color: 'var(--rp-text-secondary)' }}>Pending Re-consent</div>
          <div className="text-lg font-bold" style={{ color: consentStats?.pending_reconsent ? 'var(--admin-warning)' : 'var(--rp-text)' }}>
            {consentStats?.pending_reconsent ?? '—'}
          </div>
        </div>
      </div>

      {/* Document Metadata */}
      {editingContent && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label htmlFor="legal-entity-name" className="block text-xs font-semibold mb-1" style={{ color: 'var(--rp-text-secondary)' }}>
              Legal Entity Name
            </label>
            <input
              id="legal-entity-name"
              type="text"
              value={editingContent.entity_name}
              onChange={e => setEditingContent({ ...editingContent, entity_name: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border bg-transparent text-sm"
              style={{ borderColor: 'var(--rp-border)', color: 'var(--rp-text)' }}
            />
          </div>
          <div>
            <label htmlFor="legal-effective-date" className="block text-xs font-semibold mb-1" style={{ color: 'var(--rp-text-secondary)' }}>
              Effective Date
            </label>
            <input
              id="legal-effective-date"
              type="date"
              value={editingContent.effective_date}
              onChange={e => setEditingContent({ ...editingContent, effective_date: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border bg-transparent text-sm"
              style={{ borderColor: 'var(--rp-border)', color: 'var(--rp-text)' }}
            />
          </div>
          <div>
            <label htmlFor="legal-min-age" className="block text-xs font-semibold mb-1" style={{ color: 'var(--rp-text-secondary)' }}>
              Minimum Age Requirement
            </label>
            <input
              id="legal-min-age"
              type="number"
              value={editingContent.min_age}
              onChange={e => setEditingContent({ ...editingContent, min_age: parseInt(e.target.value) || 16 })}
              className="w-full px-3 py-2 rounded-lg border bg-transparent text-sm"
              style={{ borderColor: 'var(--rp-border)', color: 'var(--rp-text)' }}
            />
          </div>
        </div>
      )}

      <LegalSectionsEditor model={model} />
      <LegalPublishModal model={model} />
    </div>
  );
};
